import { useWorkspaceText } from '../interface-copy';
// The canvas renderer: one component per layout in the deck schema.
//
// The union in types.ts has a member per layout, so a forgotten layout fails to
// compile rather than rendering an empty slide. Nothing here sets a type size
// or a colour — every piece of text wears a role class from deck.css, which is
// held to the exporter's roles by test/workspace-deck-theme.mjs.
import type { DeckChart, DeckSlide } from '../types';
import { DeckChartPlot, DeckLegend } from './chart';
import { CONTENT_WIDTH, contentBox } from './frame';

type Bands = { title?: boolean; lead?: boolean; takeaway?: boolean; source?: boolean };

function Header({ title, lead }: { title: string; lead?: string }) {
  return (
    <div className="deck-header">
      <h2 className="deck-title">{title}</h2>
      {lead && <p className="deck-lead">{lead}</p>}
    </div>
  );
}

function Footer({ takeaway, source }: { takeaway?: string; source?: string }) {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  if (!takeaway && !source) return null;
  return (
    <div className="deck-footer">
      {takeaway && (
        <>
          <span className="deck-takeaway-rule" aria-hidden="true" />
          <p className="deck-takeaway">{takeaway}</p>
        </>
      )}
      {source && (
        <span className="deck-source">{uiRich('Source: {value1}', { value1: source })}</span>
      )}
    </div>
  );
}

// The content box, positioned from the same bands the exporter measures. The
// arithmetic itself is in deck.css so both renderers read one expression.
function Content({
  bands,
  fill,
  children,
}: {
  bands: Bands;
  fill?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div
      className={fill ? 'deck-content fill' : 'deck-content'}
      data-title={bands.title ? 'true' : undefined}
      data-lead={bands.lead ? 'true' : undefined}
      data-takeaway={bands.takeaway ? 'true' : undefined}
      data-source={bands.source ? 'true' : undefined}
    >
      {children}
    </div>
  );
}

function Cover({ slide }: { slide: Extract<DeckSlide, { layout: 'cover' }> }) {
  return (
    <>
      <div className="deck-field-bg" />
      <span className="deck-cover-mark" aria-hidden="true" />
      <div className="deck-hero cover">
        {slide.eyebrow && <span className="deck-eyebrow-field">{slide.eyebrow}</span>}
        <h1 className="deck-cover-title">{slide.title}</h1>
        {slide.subtitle && <p className="deck-cover-subtitle">{slide.subtitle}</p>}
      </div>
      {slide.footnote && (
        <span className="deck-corner-footnote deck-field-footnote">{slide.footnote}</span>
      )}
    </>
  );
}

function Section({ slide }: { slide: Extract<DeckSlide, { layout: 'section' }> }) {
  return (
    <>
      <div className="deck-field-bg" />
      <div className="deck-hero section">
        {slide.eyebrow && <span className="deck-eyebrow-field">{slide.eyebrow}</span>}
        <h1 className="deck-section-title">{slide.title}</h1>
        {slide.support && <p className="deck-section-support">{slide.support}</p>}
      </div>
    </>
  );
}

function Statement({ slide }: { slide: Extract<DeckSlide, { layout: 'statement' }> }) {
  return (
    <div className="deck-hero statement">
      <span className="deck-takeaway-rule" aria-hidden="true" />
      <p className="deck-statement">{slide.statement}</p>
      {slide.support && <p className="deck-statement-support">{slide.support}</p>}
    </div>
  );
}

function Quote({ slide }: { slide: Extract<DeckSlide, { layout: 'quote' }> }) {
  return (
    <>
      <div className="deck-panel-bg" />
      <figure className="deck-hero quote">
        <blockquote className="deck-quote">{`“${slide.quote}”`}</blockquote>
        {(slide.attribution || slide.role) && (
          <figcaption className="deck-quote-by">
            {slide.attribution && (
              <span className="deck-quote-attribution">{slide.attribution}</span>
            )}
            {slide.role && <span className="deck-quote-role">{slide.role}</span>}
          </figcaption>
        )}
      </figure>
    </>
  );
}

function Closing({ slide }: { slide: Extract<DeckSlide, { layout: 'closing' }> }) {
  return (
    <>
      <div className="deck-field-bg" />
      <div className="deck-hero closing">
        <h1 className="deck-closing-title">{slide.title}</h1>
        {slide.support && <p className="deck-closing-support">{slide.support}</p>}
        {slide.asks && slide.asks.length > 0 && (
          <div className="deck-asks">
            {slide.asks.map((ask) => (
              <p className="deck-ask" key={ask}>
                <span className="deck-ask-text">{ask}</span>
              </p>
            ))}
          </div>
        )}
      </div>
      {slide.footnote && (
        <span className="deck-corner-footnote deck-field-footnote">{slide.footnote}</span>
      )}
    </>
  );
}

function Agenda({ slide }: { slide: Extract<DeckSlide, { layout: 'agenda' }> }) {
  return (
    <>
      <Header title={slide.title} />
      <Content bands={{ title: true }}>
        <ol className="deck-rows agenda">
          {slide.items.map((item, at) => (
            <li className="deck-row" key={item.label}>
              <span className="deck-row-index deck-item-index">
                {String(at + 1).padStart(2, '0')}
              </span>
              <span className="deck-row-body">
                <span className="deck-item-text">{item.label}</span>
                {item.detail && <span className="deck-item-detail">{item.detail}</span>}
              </span>
            </li>
          ))}
        </ol>
      </Content>
    </>
  );
}

function Bullets({ slide }: { slide: Extract<DeckSlide, { layout: 'bullets' }> }) {
  return (
    <>
      <Header title={slide.title} lead={slide.lead} />
      <Content bands={{ title: true, lead: !!slide.lead, takeaway: !!slide.takeaway }}>
        <ul className="deck-rows bullets">
          {slide.bullets.map((bullet) => (
            <li className="deck-row" key={bullet.text}>
              <span className="deck-dot" aria-hidden="true" />
              <span className="deck-row-body">
                <span className="deck-item-text">{bullet.text}</span>
                {bullet.detail && <span className="deck-item-detail">{bullet.detail}</span>}
              </span>
            </li>
          ))}
        </ul>
      </Content>
      <Footer takeaway={slide.takeaway} />
    </>
  );
}

function TwoColumn({ slide }: { slide: Extract<DeckSlide, { layout: 'two-column' }> }) {
  return (
    <>
      <Header title={slide.title} lead={slide.lead} />
      <Content bands={{ title: true, lead: !!slide.lead, takeaway: !!slide.takeaway }}>
        <div className="deck-columns">
          {slide.columns.map((column) => (
            <div className="deck-column" key={column.heading}>
              <span className="deck-column-rule" aria-hidden="true" />
              <h3 className="deck-column-heading">{column.heading}</h3>
              {column.body ? (
                <p className="deck-body-text">{column.body}</p>
              ) : (
                <ul className="deck-column-list">
                  {(column.bullets ?? []).map((point) => (
                    <li className="deck-row" key={point}>
                      <span className="deck-dot small" aria-hidden="true" />
                      <span className="deck-body-text">{point}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      </Content>
      <Footer takeaway={slide.takeaway} />
    </>
  );
}

function Comparison({ slide }: { slide: Extract<DeckSlide, { layout: 'comparison' }> }) {
  return (
    <>
      <Header title={slide.title} lead={slide.lead} />
      <Content bands={{ title: true, lead: !!slide.lead, takeaway: !!slide.takeaway }}>
        <div className="deck-cards" style={{ gridTemplateColumns: '1fr 1fr', gap: 40 }}>
          {[slide.left, slide.right].map((side, at) => (
            <div className={at ? 'deck-card side quiet' : 'deck-card side'} key={side.heading}>
              <h3 className="deck-column-heading">{side.heading}</h3>
              <ul className="deck-points">
                {side.points.map((point) => (
                  <li className="deck-row" key={point}>
                    <span className="deck-dot small quiet" aria-hidden="true" />
                    <span className="deck-body-text">{point}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </Content>
      <Footer takeaway={slide.takeaway} />
    </>
  );
}

function StatTrio({ slide }: { slide: Extract<DeckSlide, { layout: 'stat-trio' }> }) {
  return (
    <>
      <Header title={slide.title} lead={slide.lead} />
      <Content bands={{ title: true, lead: !!slide.lead, takeaway: !!slide.takeaway }}>
        <div
          className="deck-cards"
          style={{ gridTemplateColumns: `repeat(${slide.stats.length}, 1fr)` }}
        >
          {slide.stats.map((stat) => (
            <div className="deck-card stat" key={stat.label}>
              <span className="deck-stat-value">{stat.value}</span>
              <span className="deck-stat-label">{stat.label}</span>
              {stat.delta && <span className="deck-stat-delta">{stat.delta}</span>}
              {stat.note && <span className="deck-stat-note">{stat.note}</span>}
            </div>
          ))}
        </div>
      </Content>
      <Footer takeaway={slide.takeaway} />
    </>
  );
}

function KpiGrid({ slide }: { slide: Extract<DeckSlide, { layout: 'kpi-grid' }> }) {
  const columns = slide.kpis.length <= 4 ? 2 : 3;
  return (
    <>
      <Header title={slide.title} />
      <Content bands={{ title: true, takeaway: !!slide.takeaway }} fill>
        <div
          className="deck-cards kpi"
          style={{
            gridTemplateColumns: `repeat(${columns}, 1fr)`,
            gridTemplateRows: `repeat(${Math.ceil(slide.kpis.length / columns)}, 1fr)`,
            flex: 1,
          }}
        >
          {slide.kpis.map((kpi) => (
            <div className="deck-card kpi" key={kpi.label}>
              <span className="deck-kpi-value">{kpi.value}</span>
              <span className="deck-kpi-label">{kpi.label}</span>
              {kpi.note && <span className="deck-kpi-note">{kpi.note}</span>}
            </div>
          ))}
        </div>
      </Content>
      <Footer takeaway={slide.takeaway} />
    </>
  );
}

function Timeline({ slide }: { slide: Extract<DeckSlide, { layout: 'timeline' }> }) {
  const width = CONTENT_WIDTH / slide.steps.length;
  return (
    <>
      <Header title={slide.title} lead={slide.lead} />
      <Content bands={{ title: true, lead: !!slide.lead, takeaway: !!slide.takeaway }}>
        <div
          className="deck-timeline"
          style={{ gridTemplateColumns: `repeat(${slide.steps.length}, 1fr)` }}
        >
          <span
            className="deck-timeline-track"
            style={{ width: width * (slide.steps.length - 1) + 56 }}
            aria-hidden="true"
          />
          {slide.steps.map((step) => (
            <div className="deck-step" key={step.label}>
              {step.marker && <span className="deck-step-marker">{step.marker}</span>}
              <span className="deck-step-dot" aria-hidden="true" />
              <span className="deck-step-label">{step.label}</span>
              {step.detail && <span className="deck-step-body">{step.detail}</span>}
            </div>
          ))}
        </div>
      </Content>
      <Footer takeaway={slide.takeaway} />
    </>
  );
}

function Table({ slide }: { slide: Extract<DeckSlide, { layout: 'table' }> }) {
  // A right-aligned numeric column needs less room than a label column; the
  // exporter weights them the same way.
  const weights = slide.columns.map((column) => (column.align === 'right' ? 1 : 1.6));
  const total = weights.reduce((sum, weight) => sum + weight, 0);
  const rowHeight = Math.min(
    44,
    (contentBox({
      title: true,
      lead: !!slide.lead,
      takeaway: !!slide.takeaway,
      source: !!slide.source,
    }).height -
      34) /
      slide.rows.length,
  );
  return (
    <>
      <Header title={slide.title} lead={slide.lead} />
      <Content
        bands={{
          title: true,
          lead: !!slide.lead,
          takeaway: !!slide.takeaway,
          source: !!slide.source,
        }}
        fill
      >
        <table className="deck-table">
          <colgroup>
            {weights.map((weight, at) => (
              <col key={at} style={{ width: `${(weight / total) * 100}%` }} />
            ))}
          </colgroup>
          <thead>
            <tr>
              {slide.columns.map((column) => (
                <th
                  className={column.align === 'right' ? 'deck-table-head right' : 'deck-table-head'}
                  key={column.label}
                  scope="col"
                >
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {slide.rows.map((row, at) => (
              <tr key={at} style={{ height: rowHeight }}>
                {row.slice(0, slide.columns.length).map((cell, column) => (
                  <td
                    className={
                      slide.columns[column].align === 'right'
                        ? 'deck-table-cell right'
                        : 'deck-table-cell'
                    }
                    key={column}
                  >
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </Content>
      <Footer takeaway={slide.takeaway} source={slide.source} />
    </>
  );
}

function Chart({ slide }: { slide: Extract<DeckSlide, { layout: 'chart' }> }) {
  const chart: DeckChart = slide.chart;
  const multi = chart.series.length > 1;
  const bands = {
    title: true,
    lead: !!slide.lead,
    takeaway: !!slide.takeaway,
    source: !!slide.source,
  };
  const box = contentBox(bands);
  return (
    <>
      <Header title={slide.title} lead={slide.lead} />
      <Content bands={bands} fill>
        <div className="deck-chart">
          {/* A legend is always present for two or more series: identity is
              never carried by colour alone. One series needs none — the title
              names it. */}
          {multi && <DeckLegend chart={chart} />}
          <DeckChartPlot
            chart={chart}
            box={{ width: box.width, height: box.height - (multi ? 30 : 0) }}
          />
        </div>
      </Content>
      <Footer takeaway={slide.takeaway} source={slide.source} />
    </>
  );
}

export function DeckSlideView({ slide }: { slide: DeckSlide }) {
  return (
    <div className="deck-slide" data-layout={slide.layout}>
      {slide.layout === 'cover' ? (
        <Cover slide={slide} />
      ) : slide.layout === 'section' ? (
        <Section slide={slide} />
      ) : slide.layout === 'statement' ? (
        <Statement slide={slide} />
      ) : slide.layout === 'quote' ? (
        <Quote slide={slide} />
      ) : slide.layout === 'closing' ? (
        <Closing slide={slide} />
      ) : slide.layout === 'agenda' ? (
        <Agenda slide={slide} />
      ) : slide.layout === 'bullets' ? (
        <Bullets slide={slide} />
      ) : slide.layout === 'two-column' ? (
        <TwoColumn slide={slide} />
      ) : slide.layout === 'comparison' ? (
        <Comparison slide={slide} />
      ) : slide.layout === 'stat-trio' ? (
        <StatTrio slide={slide} />
      ) : slide.layout === 'kpi-grid' ? (
        <KpiGrid slide={slide} />
      ) : slide.layout === 'timeline' ? (
        <Timeline slide={slide} />
      ) : slide.layout === 'table' ? (
        <Table slide={slide} />
      ) : slide.layout === 'chart' ? (
        <Chart slide={slide} />
      ) : (
        // Not a fallback: `slide` is `never` here, so adding a layout to the
        // schema without one here fails to compile. A catch-all `else` would
        // instead have drawn the new layout as a chart and looked deliberate.
        exhausted(slide)
      )}
    </div>
  );
}

function exhausted(slide: never): never {
  throw new Error(`no canvas renderer for layout ${JSON.stringify(slide)}`);
}
